import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E07 — TISSEUR. Le seul qui donne un ordre.
 *
 *  Tous les autres ennemis du bestiaire interdisent : une zone, un angle, un
 *  instant. Le Tisseur, lui, PRESCRIT — il ne laisse qu'un passage et il faut y
 *  aller. C'est la seule fois où le jeu retire au joueur le choix de son
 *  itinéraire, et c'est pour ça qu'il n'y en a qu'un dans tout le bestiaire.
 *
 *  ── Ce qu'il enseigne ──
 *  À lire une forme AVANT qu'elle n'arrive, et à s'y engager tôt. Un mur se lit
 *  en un coup d'œil quand il est loin et devient illisible quand il est proche :
 *  le joueur qui attend perd, celui qui décide à mi-distance passe sans
 *  ralentir. C'est la première fois du jeu où hésiter coûte plus cher que se
 *  tromper.
 *
 *  ── Pourquoi le trou glisse toujours dans le même sens ──
 *  C'est LA décision qui fait la différence entre un bon et un mauvais Tisseur.
 *
 *  Un trou qui se replacerait au hasard à chaque volée forcerait à s'arrêter,
 *  relire, repartir — trois murs, trois arrêts, et le joueur passe son temps
 *  immobile à décoder. Un trou qui glisse de 90 u dans la même direction sur
 *  trois murs consécutifs produit l'inverse : le joueur qui a compris le sens
 *  se déplace AVEC lui et traverse les trois sans jamais s'arrêter. Le pattern
 *  devient un mouvement, et l'inversion tous les trois murs est le seul moment
 *  où il faut vraiment regarder.
 *
 *  Même densité de projectiles, même difficulté nominale, sensation opposée.
 *
 *  ── Pourquoi le mur ne barre pas tout le terrain ──
 *  Un mur pleine largeur à trou unique ne laisserait qu'un seul couloir viable,
 *  ce que PLAN.md §6.5 interdit. Le mur s'arrête donc à 650 u sur 900, laissant
 *  125 u de flanc de chaque côté quand le Tisseur est centré : trois passages
 *  au lieu d'un. Le trou reste le bon choix — il est central et il est large —
 *  mais il n'est jamais le SEUL, et un joueur acculé garde toujours une sortie.
 *
 *  ⚠️ PIÈGE DE VAGUE : la règle vaut pour UN Tisseur. Deux Tisseurs alignés
 *  horizontalement barrent le terrain ensemble et reconstituent exactement la
 *  situation que le mur partiel évite. En quinconce, jamais côte à côte.
 *
 *  ── Lent et solide, délibérément ──
 *  90 u/s : il reste à l'écran une quinzaine de secondes et pose six murs. 400
 *  PV : on peut le supprimer, mais pas d'un revers de main, et l'ignorer est un
 *  choix défendable. Le rendre fragile en ferait une formalité ; le rendre
 *  rapide supprimerait le temps de lecture dont son pattern a besoin. */
export default defineEnemy({
  id: 'tisseur',
  rank: Rank.Standard,
  hp: 400,
  visualRadius: 38,
  score: 340,
  telegraph: 1.1,
  drops: 3,
  motion: { kind: Motion.Linear, speed: 90 },
  emitter: {
    pattern: Pattern.Wall,
    every: 2.6,
    /* 11 emplacements sur 650 u ⇒ un pas de 65 u, donc un trou de 130 u
       exactement — la largeur annoncée par PLAN.md §6.4. Elle est DÉRIVÉE de la
       géométrie du mur et non déclarée : le moteur retire l'emplacement le plus
       proche du centre du trou, ce qui garantit deux pas de vide quelle que
       soit la position. Changer `count` ou `width` change donc le trou ;
       recalculer 2 × width / (count − 1) avant de toucher à l'un ou à l'autre. */
    count: 11,
    width: 650,
    slide: 90,
    /* 220 u/s : dans la bande 180-300 réservée aux murs. Un mur rapide est une
       contradiction — sa raison d'être est de laisser le temps de choisir. À
       cette vitesse il met 5,5 s à traverser le terrain, soit deux volées de
       marge pour se replacer. */
    speed: 220,
    firstDelay: 1.8,
    family: Fam.EnemyRed,
  },
});
